// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Stack, Center } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { IconArrowLeft, IconColumns, IconCheck, IconLock, IconShieldCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { SplitScreenLayout, PasswordStrength } from '../components';

const testimonial = {
  quote: "The security measures here are top-notch. I feel completely confident that my data is protected at all times.",
  author: "Michael Chen",
  role: "Security Engineer at DataFlow",
  avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&q=80",
};

export default function ResetSplitScreen() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === confirmPassword) {
      setIsReset(true);
    }
  };

  return (
    <SplitScreenLayout
      ratio="50-50"
      leftContentType="testimonial"
      gradientFrom="green"
      gradientTo="teal"
      testimonial={testimonial}
      logo={
        <Center
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
          }}
        >
          <IconShieldCheck size={32} color="white" />
        </Center>
      }
    >
      <Container size="xs" w="100%">
        <Stack gap="lg">
          <Button
            component={Link}
            to="/app/demo/auth-pages/reset-password"
            variant="subtle"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            w="fit-content"
          >
            Back to variants
          </Button>

          {isReset ? (
            <FadeIn>
              <Stack gap="lg" align="center">
                <Center
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 20,
                    backgroundColor: 'var(--mantine-color-green-light)',
                  }}
                >
                  <IconCheck size={40} color="var(--mantine-color-green-6)" />
                </Center>

                <Stack align="center" gap="xs">
                  <Title order={2} ta="center">Password reset!</Title>
                  <Text c="dimmed" ta="center" maw={300}>
                    Your password has been successfully reset. You can now sign in with your new password.
                  </Text>
                </Stack>

                <Button
                  component={Link}
                  to="/app/demo/auth-pages/login/splitscreen"
                  fullWidth
                  size="md"
                  variant="gradient"
                  gradient={{ from: 'green', to: 'teal' }}
                >
                  Sign in
                </Button>
              </Stack>
            </FadeIn>
          ) : (
            <>
              <FadeIn delay={100}>
                <Stack gap="xs" align="center">
                  <Badge
                    leftSection={<IconColumns size={12} />}
                    variant="gradient"
                    gradient={{ from: 'green', to: 'teal' }}
                    size="lg"
                  >
                    Split Screen
                  </Badge>
                  <Center
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 14,
                      backgroundColor: 'var(--mantine-color-green-light)',
                      marginTop: 'var(--mantine-spacing-sm)',
                    }}
                  >
                    <IconLock size={28} color="var(--mantine-color-green-6)" />
                  </Center>
                  <Title order={2} ta="center" mt="sm">
                    Set new password
                  </Title>
                  <Text c="dimmed" size="sm" ta="center" maw={320}>
                    Your new password must be different from previously used passwords.
                  </Text>
                </Stack>
              </FadeIn>

              <SlideIn direction="up" delay={200}>
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <PasswordInput
                      label="New password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />

                    {password && <PasswordStrength password={password} variant="bars" />}

                    <PasswordInput
                      label="Confirm new password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      size="md"
                      variant="gradient"
                      gradient={{ from: 'green', to: 'teal' }}
                      disabled={!password || password !== confirmPassword}
                    >
                      Reset password
                    </Button>
                  </Stack>
                </form>
              </SlideIn>

              <FadeIn delay={300}>
                <Center>
                  <Anchor component={Link} to="/app/demo/auth-pages/login/splitscreen" size="sm">
                    <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                    Back to login
                  </Anchor>
                </Center>
              </FadeIn>
            </>
          )}
        </Stack>
      </Container>
    </SplitScreenLayout>
  );
}
